import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  writeBatch,
  deleteDoc,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { Notification, NotificationType, NotificationData } from '@/lib/types/notifications';
import { toast } from 'react-hot-toast';
import { desktopNotificationService } from './desktopNotificationService';
import { userNotificationPreferencesService } from './userNotificationPreferencesService';

export class NotificationService {
  private static instance: NotificationService;
  private activeListeners: Map<string, Unsubscribe> = new Map();
  private desktopNotificationsEnabled: boolean = false;
  
  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Initialize desktop notifications
   */
  public async initializeDesktopNotifications(): Promise<boolean> {
    if (!desktopNotificationService.isDesktopNotificationSupported()) {
      console.warn('Desktop notifications are not supported');
      return false;
    }

    const permission = await desktopNotificationService.requestPermission();
    this.desktopNotificationsEnabled = permission === 'granted';
    
    if (this.desktopNotificationsEnabled) {
      console.log('Desktop notifications enabled');
    } else {
      console.warn('Desktop notifications permission denied');
    }
    
    return this.desktopNotificationsEnabled;
  }

  /**
   * Enable desktop notifications
   */
  public async enableDesktopNotifications(): Promise<boolean> {
    return await this.initializeDesktopNotifications();
  }

  /**
   * Disable desktop notifications
   */
  public disableDesktopNotifications(): void {
    this.desktopNotificationsEnabled = false;
  }

  /**
   * Check if desktop notifications are enabled
   */
  public areDesktopNotificationsEnabled(): boolean {
    return this.desktopNotificationsEnabled && 
           desktopNotificationService.getPermissionStatus() === 'granted';
  }

  /**
   * Get desktop notification permission status
   */
  public getDesktopNotificationPermissionStatus(): NotificationPermission {
    return desktopNotificationService.getPermissionStatus();
  }

  // Create a new notification
  async createNotification(
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    data?: NotificationData,
    actionUrl?: string,
    actionText?: string
  ): Promise<string> {
    try {
      // Check if user has notifications enabled (simple localStorage check)
      const userSettings = localStorage.getItem(`userNotificationSettings_${userId}`);
      if (userSettings) {
        const settings = JSON.parse(userSettings);
        if (!settings.notificationsEnabled) {
          console.log(`Skipping notification for user ${userId} - notifications disabled`);
          return ''; // Return empty string if notification is disabled
        }
      }

      const notification: Omit<Notification, 'id'> = {
        userId,
        type,
        title,
        message,
        data: data || {},
        read: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        actionUrl,
        actionText
      };

      const docRef = await addDoc(collection(db, 'notifications'), notification);
      
      // Show desktop notification if enabled
      if (this.areDesktopNotificationsEnabled()) {
        const fullNotification: Notification = {
          id: docRef.id,
          ...notification
        };
        
        await desktopNotificationService.showNotificationFromAppData(fullNotification);
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  // Create notifications for multiple users (bulk)
  async createBulkNotifications(
    userIds: string[],
    type: NotificationType,
    title: string,
    message: string,
    data?: NotificationData,
    actionUrl?: string,
    actionText?: string
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      const notificationsRef = collection(db, 'notifications');
      const notifications: Array<{ docRef: any, notification: Omit<Notification, 'id'> }> = [];

      userIds.forEach(userId => {
        const notification: Omit<Notification, 'id'> = {
          userId,
          type,
          title,
          message,
          data: data || {},
          read: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          actionUrl,
          actionText
        };

        const docRef = doc(notificationsRef);
        batch.set(docRef, notification);
        notifications.push({ docRef, notification });
      });

      await batch.commit();
      
      // Show desktop notifications if enabled
      if (this.areDesktopNotificationsEnabled()) {
        for (const { docRef, notification } of notifications) {
          const fullNotification: Notification = {
            id: docRef.id,
            ...notification
          };
          
          await desktopNotificationService.showNotificationFromAppData(fullNotification);
        }
      }
    } catch (error) {
      console.error('Error creating bulk notifications:', error);
      throw error;
    }
  }

  // Get notifications for a user
  async getUserNotifications(userId: string, limitCount: number = 50): Promise<Notification[]> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const notifications = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[];
      
      // Sort by createdAt in descending order (newest first)
      return notifications.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  // Get unread notifications count
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  // Delete notification
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await deleteDoc(notificationRef);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  // Clear (delete) all notifications for a user
  async clearAllNotifications(userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId)
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.docs.forEach((docSnapshot) => {
        batch.delete(docSnapshot.ref);
      });

      await batch.commit();
      console.log(`Cleared ${snapshot.docs.length} notifications for user: ${userId}`);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  }

  // Mark all notifications as read for a user
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);

      querySnapshot.docs.forEach(doc => {
        batch.update(doc.ref, {
          read: true,
          updatedAt: new Date().toISOString()
        });
      });

      await batch.commit();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  // Helper methods for specific notification types
  async notifyTaskAssignment(
    assigneeIds: string[],
    taskTitle: string,
    assignedBy: string,
    taskId: string,
    dueDate?: string
  ): Promise<void> {
    await this.createBulkNotifications(
      assigneeIds,
      NotificationType.TaskAssigned,
      'New Task Assignment',
      `${assignedBy} assigned you to: ${taskTitle}`,
      {
        taskId,
        assignedBy,
        dueDate
      },
      `/tasks/${taskId}`,
      'View Task'
    );
  }

  async notifyEventInvite(
    attendeeIds: string[],
    eventTitle: string,
    createdBy: string,
    eventId: string,
    eventDate: string
  ): Promise<void> {
    await this.createBulkNotifications(
      attendeeIds,
      NotificationType.EventInvite,
      'Calendar Event Invitation',
      `${createdBy} invited you to: ${eventTitle}`,
      {
        eventId,
        assignedBy: createdBy,
        dueDate: eventDate
      },
      `/group-calendar`,
      'View Calendar'
    );
  }

  async notifyTemplateReview(
    reviewerId: string,
    templateId: string,
    submittedBy: string
  ): Promise<void> {
    await this.createNotification(
      reviewerId,
      NotificationType.TemplateReview,
      'Template Ready for Review',
      `${submittedBy} submitted a template for review`,
      {
        templateId,
        assignedBy: submittedBy
      },
      `/admin/templates/${templateId}`,
      'Review Template'
    );
  }

  async notifyTemplateCorrections(
    submitterId: string,
    templateId: string,
    reviewedBy: string,
    correctionNotes: string
  ): Promise<void> {
    await this.createNotification(
      submitterId,
      NotificationType.TemplateCorrections,
      'Template Needs Corrections',
      `${reviewedBy} requested corrections on your template`,
      {
        templateId,
        reviewerId: reviewedBy,
        correctionNotes
      },
      `/templates/${templateId}`,
      'Fix Template'
    );
  }

  async notifyPhotoUpload(
    userId: string,
    photoCount: number,
    uploadDate: string
  ): Promise<void> {
    await this.createNotification(
      userId,
      NotificationType.PhotosUploaded,
      'Photos Processed',
      `You uploaded ${photoCount} photos today`,
      {
        photoCount,
        uploadDate
      },
      '/photo-log',
      'View Photos'
    );
  }

  // Check if notification already exists for a specific message
  async notificationExists(
    userId: string,
    type: NotificationType,
    chatId: string,
    messageId: string
  ): Promise<boolean> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('type', '==', type),
        where('data.chatId', '==', chatId),
        where('data.messageId', '==', messageId)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.size > 0;
    } catch (error) {
      console.error('Error checking notification existence:', error);
      return false;
    }
  }

  async notifyFinChatMessage(
    userId: string,
    senderId: string,
    senderName: string,
    messagePreview: string,
    chatId: string,
    messageId?: string
  ): Promise<void> {
    // Check if notification already exists for this specific message
    if (messageId) {
      const exists = await this.notificationExists(
        userId,
        NotificationType.FinChatMessage,
        chatId,
        messageId
      );
      if (exists) {
        return; // Skip creating duplicate notification
      }
    }

    await this.createNotification(
      userId,
      NotificationType.FinChatMessage,
      'New FIN Chat Message',
      `${senderName}: ${messagePreview}`,
      {
        chatId,
        senderId,
        messagePreview,
        messageId
      },
      `/chats/${chatId}`,
      'View Chat'
    );
  }

  async notifyVideoChatInvite(
    inviteeIds: string[],
    invitedBy: string,
    invitedByName: string,
    workspaceTitle: string,
    workspaceId: string,
    sessionId: string
  ): Promise<void> {
    await this.createBulkNotifications(
      inviteeIds,
      NotificationType.VideoChatInvite,
      '📹 Video Chat Invitation',
      `${invitedByName} invited you to join a video session in "${workspaceTitle}"`,
      {
        sessionId,
        workspaceId,
        invitedBy,
        invitedByName,
        sessionType: 'brainstorm'
      },
      `/brainstorm?workspace=${workspaceId}`,
      'Join Session'
    );
  }

  // Helper function to get users by specific roles
  async getUsersByRoles(roles: string[]): Promise<string[]> {
    try {
      const userEmails: string[] = [];
      
      for (const role of roles) {
        const q = query(
          collection(db, 'users'),
          where('roles', 'array-contains', role)
        );
        const querySnapshot = await getDocs(q);
        
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          if (userData.email && !userEmails.includes(userData.email)) {
            userEmails.push(userData.email);
          }
        });
      }
      
      return userEmails;
    } catch (error) {
      console.error('Error fetching users by roles:', error);
      return [];
    }
  }

  /**
   * Tell the store a jeweler has sent back their final quote on a repair.
   * `needsApproval` is false when the quote came in at or under the amount the
   * customer already authorized, so staff know whether a call is required.
   */
  async notifyRepairQuoteReady(
    repairId: string,
    repairNumber: string,
    customerName: string,
    jewelerName: string,
    quote: number,
    needsApproval: boolean
  ): Promise<void> {
    try {
      const targetRoles = [
        'Master Admin',
        'Repair Admin',
        'Store Manager',
        'Store Staff',
        'Tech',
        'CS Admin',
      ];
      const staffEmails = await this.getUsersByRoles(targetRoles);

      if (staffEmails.length === 0) {
        console.warn('No users found with repair roles for quote notification');
        return;
      }

      const amount = `$${quote.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

      await this.createBulkNotifications(
        staffEmails,
        NotificationType.RepairQuoteReady,
        needsApproval ? 'Repair Quote — Customer Approval Needed' : 'Repair Quote Ready',
        needsApproval
          ? `${jewelerName} quoted ${amount} on ${repairNumber} (${customerName}) — over the authorized amount, call the customer.`
          : `${jewelerName} quoted ${amount} on ${repairNumber} (${customerName}) — within the authorized amount.`,
        {
          repairId,
          repairNumber,
          customerName,
          jewelerName,
          quote,
          needsApproval,
        },
        `/repairs/${repairId}`,
        'View Repair'
      );
    } catch (error) {
      console.error('Error sending repair quote notifications:', error);
      // Never let a failed alert roll back the recorded quote.
    }
  }

  // Helper method for jewelry request notifications
  async notifyJewelryRequestSubmitted(
    requesterName: string,
    requesterEmail: string,
    requestType: string,
    skuCount: number,
    needByDate: string,
    store: string,
    requestId: string
  ): Promise<void> {
    try {
      // Get users with Master Admin, CS Admin, and Template roles
      const targetRoles = ['Master Admin', 'CS Admin', 'Template'];
      const adminEmails = await this.getUsersByRoles(targetRoles);
      
      if (adminEmails.length === 0) {
        console.warn('No users found with target roles for jewelry request notification');
        return;
      }

      await this.createBulkNotifications(
        adminEmails,
        NotificationType.JewelryRequestSubmitted,
        'New Jewelry Request',
        `${requesterName} submitted a ${requestType.toLowerCase()} request for ${skuCount} SKU${skuCount > 1 ? 's' : ''} - needed by ${needByDate}`,
        {
          requestId,
          requesterName,
          requesterEmail,
          requestType,
          skuCount,
          needByDate,
          store
        },
        '/jewelry-requests',
        'View Request'
      );
    } catch (error) {
      console.error('Error sending jewelry request notifications:', error);
      throw error;
    }
  }

  // Content Creation Notifications
  async notifyContentSubmitted(
    contentId: string,
    contentTitle: string,
    submittedBy: string,
    submittedByName: string,
    platforms: string[]
  ): Promise<void> {
    try {
      // Get all users with roles: Master Admin, Marketing, Marketing Admin
      const reviewerEmails = await this.getUsersByRoles(['Master Admin', 'Marketing', 'Marketing Admin']);

      if (reviewerEmails.length === 0) {
        console.warn('No users found with required roles for content review notifications');
        return;
      }

      const platformText = platforms.join(', ');
      
      await this.createBulkNotifications(
        reviewerEmails,
        NotificationType.ContentSubmitted,
        '📱 New Content for Review',
        `${submittedByName} submitted "${contentTitle}" for ${platformText}`,
        {
          contentId,
          contentTitle,
          submittedBy,
          submittedByName,
          platforms
        },
        '/content-creation',
        'Review Content'
      );
    } catch (error) {
      console.error('Error sending content submission notifications:', error);
      throw error;
    }
  }

  async notifyContentReviewed(
    contentId: string,
    contentTitle: string,
    submittedBy: string,
    submittedByName: string,
    reviewedBy: string,
    reviewedByName: string,
    action: 'approved' | 'denied' | 'rework_requested',
    feedback?: string
  ): Promise<void> {
    try {
      let notificationType: NotificationType;
      let title: string;
      let message: string;
      let emoji: string;

      switch (action) {
        case 'approved':
          notificationType = NotificationType.ContentApproved;
          emoji = '✅';
          title = 'Content Approved';
          message = `Your content "${contentTitle}" has been approved by ${reviewedByName}`;
          break;
        case 'denied':
          notificationType = NotificationType.ContentDenied;
          emoji = '❌';
          title = 'Content Denied';
          message = `Your content "${contentTitle}" was denied by ${reviewedByName}`;
          break;
        case 'rework_requested':
          notificationType = NotificationType.ContentReworkRequested;
          emoji = '🔄';
          title = 'Content Rework Requested';
          message = `${reviewedByName} requested rework on "${contentTitle}"`;
          break;
      }

      await this.createNotification(
        submittedBy,
        notificationType,
        `${emoji} ${title}`,
        message,
        {
          contentId,
          contentTitle,
          submittedBy,
          submittedByName,
          reviewedBy,
          reviewedByName,
          feedback
        },
        '/content-creation',
        'View Content'
      );
    } catch (error) {
      console.error('Error sending content review notifications:', error);
      throw error;
    }
  }

  // Real-time listener for user notifications
  listenToUserNotifications(
    userId: string, 
    callback: (notifications: Notification[]) => void,
    limitCount: number = 50
  ): Unsubscribe {
    const listenerKey = `notifications_${userId}`;
    
    // Clean up any existing listener
    if (this.activeListeners.has(listenerKey)) {
      this.activeListeners.get(listenerKey)!();
      this.activeListeners.delete(listenerKey);
    }

    // Use simple query without orderBy to avoid composite index requirement
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      limit(limitCount)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log(`📧 Real-time notifications update for ${userId}:`, querySnapshot.docs.length, 'notifications');
      
      try {
        const notifications = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Notification[];
        
        // Sort in memory by createdAt (newest first)
        const sortedNotifications = notifications.sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bTime - aTime;
        });
        
        console.log('📧 Sorted notifications:', sortedNotifications.length, 'items');
        callback(sortedNotifications);
      } catch (processingError) {
        console.error('❌ Error processing notifications:', processingError);
        callback([]);
      }
    }, (error) => {
      console.error('❌ Error listening to user notifications:', error);
      console.error('❌ Error details:', {
        code: error?.code,
        message: error?.message,
        userId: userId
      });
      // Call callback with empty array on error to stop loading state
      callback([]);
    });

    this.activeListeners.set(listenerKey, unsubscribe);
    return unsubscribe;
  }

  // Real-time listener for unread count
  listenToUnreadCount(
    userId: string, 
    callback: (count: number) => void
  ): Unsubscribe {
    const listenerKey = `unread_count_${userId}`;
    
    // Clean up any existing listener
    if (this.activeListeners.has(listenerKey)) {
      this.activeListeners.get(listenerKey)!();
      this.activeListeners.delete(listenerKey);
    }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      callback(querySnapshot.size);
    }, (error) => {
      console.error('Error listening to unread count:', error);
      callback(0);
    });

    this.activeListeners.set(listenerKey, unsubscribe);
    return unsubscribe;
  }

  // Clean up listener for a specific user
  cleanupUserListeners(userId: string): void {
    const notificationsKey = `notifications_${userId}`;
    const unreadCountKey = `unread_count_${userId}`;
    
    [notificationsKey, unreadCountKey].forEach(key => {
      if (this.activeListeners.has(key)) {
        this.activeListeners.get(key)!();
        this.activeListeners.delete(key);
      }
    });
  }

  // Clean up all listeners
  cleanupAllListeners(): void {
    this.activeListeners.forEach(unsubscribe => unsubscribe());
    this.activeListeners.clear();
  }
}

export const notificationService = NotificationService.getInstance(); 